import { getSegment } from "../src/segment/helpers";
import { trackEventHandler } from "./segment/segment-calls";

/** @typedef {() => void} Callback */

/**
 * @typedef {Object} MktoFormOptions
 * @prop {string} program_name
 * @prop {string} is_pd_employee
 * @prop {string} mkwid
 * @prop {string} gclid
 * @prop {string} [name]
 * @prop {string} [email]
 */

/**
 * @typedef {Object} SlfOptions
 * @prop {string} form_type
 * @prop {string} version
 * @prop {number} step_number
 * @prop {string} step_name
 * @prop {string} step_status
 * @prop {string} failure_type
 * @prop {string} error_message
 * @prop {string} is_pd_employee
 * @prop {string} [organism]
 * @prop {string} mkwid
 * @prop {string} gclid
 */

/**
 * @typedef {Object} CFDataOptions
 * @prop {string} country
 * @prop {string} region
 * @prop {string} status
 * @prop {string} reason
 */

/**
 * @arg {SlfOptions & {account_id?: string, form_data?: Object}} options
 * @arg {Callback} [callback]
 */
export function trialFormAttemptedSignup(options, callback) {
  const props = {
    properties: {
      form_type: options["form_type"],
      failure_type: options["failure_type"],
      error_message: options["error_message"],
      is_pd_employee: options["is_pd_employee"],
      version: options["version"],
      account_id: options["account_id"],
      context_campaign_gclid: options["gclid"],
      context_campaign_mkwid: options["mkwid"],
      form_data: options["form_data"],
    },
    category: "form submitted",
    label: options["step_status"], // We use this for Google Analytics Ad words Audience Segmenting
  };
  const event = "Attempted Signup";
  if (callback) getSegment("track")(event, props, callback);
  else getSegment("track")(event, props);
}

/**
 * @arg {SlfOptions} options
 * @arg {Callback} [callback]
 */
export function trialFormDependencyActivation(options, callback) {
  const props = {
    properties: {
      form_type: options["form_type"],
      step_number: options["step_number"],
      step_name: options["step_name"],
      step_status: options["step_status"],
      failure_type: options["failure_type"],
      error_message: options["error_message"],
      is_pd_employee: options["is_pd_employee"],
      version: options["version"],
      context_campaign_gclid: options["gclid"],
      context_campaign_mkwid: options["mkwid"],
    },
    category: "infrastructure",
    label: options["step_status"], // We use this for Google Analytics Ad words Audience Segmenting
  };
  const event = "Single Line Form Dependency Activation";
  if (callback) getSegment("track")(event, props, callback);
  else getSegment("track")(event, props);
}

/**
 * @arg {SlfOptions} options
 * @arg {Callback} [callback]
 */
export function trialFormNextButtonClicked(options, callback) {
  const props = {
    properties: {
      form_type: options["form_type"],
      step_number: options["step_number"],
      step_name: options["step_name"],
      step_status: options["step_status"],
      failure_type: options["failure_type"],
      error_message: options["error_message"],
      is_pd_employee: options["is_pd_employee"],
      organism: options["organism"],
      version: options["version"],
      context_campaign_gclid: options["gclid"],
      context_campaign_mkwid: options["mkwid"],
    },
    category: "button click",
    label: options["step_status"], // We use this for Google Analytics Ad words Audience Segmenting
  };
  const event = "Single Line Form Next Button Clicked";
  if (callback) getSegment("track")(event, props, callback);
  else getSegment("track")(event, props);
}

/**
 * @arg {{[key: string]: string|number}} options
 * @arg {Callback} [callback]
 */
export function trialFormBackButtonClicked(options, callback) {
  const props = {
    form_type: options["form_type"],
    step_number: options["step_number"],
    step_name: options["step_name"],
    is_pd_employee: options["is_pd_employee"],
    version: options["version"],
    context_campaign_gclid: options["gclid"],
    context_campaign_mkwid: options["mkwid"],
    category: "button click",
  };
  const event = "Form Back Button Clicked";
  if (callback) getSegment("track")(event, props, callback);
  else getSegment("track")(event, props);
}

/**
 * @arg {{[key: string]: string|number|boolean}} options
 * @arg {Callback} [callback]
 */
export function trialFormPasswordVisibilityToggled(options, callback) {
  const props = {
    visibility: options["visibility"],
    form_type: options["form_type"],
    is_pd_employee: options["is_pd_employee"],
    version: options["version"],
    context_campaign_gclid: options["gclid"],
    context_campaign_mkwid: options["mkwid"],
  };
  const event = "Password Visibility Toggled";
  if (callback) getSegment("track")(event, props, callback);
  else getSegment("track")(event, props);
}

/**
 * @param {{ [x: string]: any; form_type?: string; failure_type?: string; error_message?: string; is_pd_employee?: string; step_number?: number; step_name?: string; step_status?: string; version?: string; current_step?: number; }} options
 * @param {undefined} [callback]
 */
export function trialFormElementUserInputDetected(options, callback) {
  const props = {
    /** @type {{ form_type?: string; version?: string; field_name: string; organism?: string; is_pd_employee?: string; }} */
    properties: {
      form_type: options["form_type"],
      version: options["version"],
      field_name: options["field_name"],
      organism: options["organism"],
    },
    category: "user input detected event",
    label: options["field_name"],
  };
  if (options["is_pd_employee"])
    props.properties["is_pd_employee"] = options["is_pd_employee"];
  const event = "Single Line Form User Input Detected";
  formElementUserInputDetected(event, props, callback);
}

/**
 * @param {string} event
 * @param {{}} props
 * @param {function | undefined} [callback]
 */
export function formElementUserInputDetected(event, props, callback) {
  if (callback) getSegment("track")(event, props, callback);
  else getSegment("track")(event, props);
}

/**
 * @param {function | undefined} [callback]
 * @param {{ [x: string]: any; }} options
 */
export function marketoFormEngagementDetected(options, callback) {
  const props = {
    /** @type {{ program_name?: string; form_type?: string; version?: string; field_name: string; is_pd_employee?: string; }} */
    properties: {
      program_name: options["program_name"],
      form_type: "Marketo Form",
      field_name: options["field_name"],
    },
  };
  const event = "Marketo Form Engagement Detected";
  if (callback) getSegment("track")(event, props, callback);
  else getSegment("track")(event, props);
}

/**
 * @param {{ [x: string]: any; name?: any; email?: any; }} options
 * @param {Callback} [callback]
 */
export function sendContactSupportFormSegTrack(options, callback) {
  const props = {
    properties: {
      name: options["name"],
      email: options["email"],
    },
    category: "form submitted",
    label: "contact support", // We use this for Google Analytics Ad words Audience Segmenting
  };
  const event = "Contact Support Form Submit";
  if (callback) getSegment("track")(event, props, callback);
  else getSegment("track")(event, props);
}

/**
 * @param {{ [x: string]: any; name?: any; email?: any; }} options
 * @param {Callback} [callback]
 */
export function sendContactSalesFormSegTrack(options, callback) {
  const props = {
    properties: {
      name: options["name"],
      email: options["email"],
    },
    category: "form submitted",
    label: "contact sales", // We use this for Google Analytics Ad words Audience Segmenting
  };
  const event = "Contact Sales Form Submit";
  if (callback) getSegment("track")(event, props, callback);
  else getSegment("track")(event, props);
}

/**
 * @param {{ [x: string]: any; name?: any; email?: any; }} options
 * @param {Callback} [callback]
 */
export function sendDemoFormSegTrack(options, callback) {
  const props = {
    properties: {
      email: options["email"],
      job_title: options["job_title"],
      country: options["country"],
      currently_in_trial: options["currently_in_trial"],
    },
    category: "form submitted",
    label: "demo signup", // We use this for Google Analytics Ad words Audience Segmenting
  };
  const event = "Demo Requested";
  if (callback) getSegment("track")(event, props, callback);
  else getSegment("track")(event, props);
}

/**
 * @param {string} formType
 */
function trialFormViewed(formType) {
  const props = {
    properties: {
      form_type: formType,
    },
  };
  getSegment("track")("Trial Form Viewed", props);
}

export function trialFullFormViewed() {
  trialFormViewed("Full Form");
}

export function trialSingleLineFormViewed() {
  trialFormViewed("Single Line Form");
}

/**
 * @arg {{}} options
 */
export function pricingCTAClicked(options) {
  getSegment("track")("Marketing Pricing CTA Clicked", options);
}

/**
 * @param {{ [x: string]: any; name?: any; videoId?: any; totalDuration?: any; createdAt?: string; avgPercentWatched?: any; }} options
 */
export function wistiaVideoPlayed(options) {
  const props = {
    title: options["name"],
    video_id: options["videoId"],
    total_duration: options["totalDuration"],
    created_at: options["createdAt"],
  };
  getSegment("track")("Wistia Video Played", props);
}

/**
 * @param {{ [x: string]: any; name?: any; videoId?: any; totalDuration?: any; createdAt?: string; avgPercentWatched?: any; }} options
 */
export function wistiaVideoPaused(options) {
  const props = {
    title: options["name"],
    video_id: options["videoId"],
    total_duration: options["totalDuration"],
    total_seconds_watched: options["totalSecondsWatched"],
  };
  getSegment("track")("Wistia Video Paused", props);
}

/**
 * @param {{ [x: string]: any; name?: any; videoId?: any; totalDuration?: any; createdAt?: string; avgPercentWatched?: any; }} options
 */
export function wistiaVideoEnded(options) {
  const props = {
    title: options["name"],
    video_id: options["videoId"],
    total_seconds_watched: options["totalSecondsWatched"],
  };
  getSegment("track")("Wistia Video Ended", props);
}

/**
 * @param {object} options
 */
export function videoPlayed(options) {
  getSegment("track")("Video Played", options);
}

/**
 * @param {object} options
 */
export function videoPaused(options) {
  getSegment("track")("Video Paused", options);
}

/**
 * @param {object} options
 */
export function videoEnded(options) {
  getSegment("track")("Video Ended", options);
}

/**
 * Since some generic event buttons sit inside injected HTML
 * you need to add an extra class to those buttons and call this
 * function after the HTML has been injected.
 */
export function setupABTestTrackClickEvents() {
  const trackEvents = document.querySelectorAll(".abtest-generic-event");
  for (let i = 0; i < trackEvents.length; ++i) {
    trackEvents[i]?.addEventListener("click", trackEventHandler);
  }
}

/**
 * @arg {MktoFormOptions} options
 * @arg {Callback} [callback]
 */
export function trackMarketoFormSubmit(options, callback) {
  /** @type {Record<string, string>}*/
  const identifyProps = {};
  if (options["name"]) identifyProps["name"] = options["name"];
  if (options["email"]) identifyProps["email"] = options["email"];
  getSegment("identify")(identifyProps);

  const props = {
    properties: {
      program_name: options["program_name"],
      context_campaign_gclid: options["gclid"],
      context_campaign_mkwid: options["mkwid"],
      is_pd_employee: options["is_pd_employee"],
    },
    category: "form submitted",
  };
  const event = "Marketo Form Submit";
  if (callback) getSegment("track")(event, props, callback);
  else getSegment("track")(event, props);
}

/**
 * @arg {CFDataOptions} options
 * @arg {Callback} [callback]
 */
export function trackCloudflareEnrichment(options, callback) {
  const props = {
    properties: {
      country: options["country"],
      region: options["region"],
      status: options["status"],
      reason: options["reason"],
    },
  };
  const event = "Cloudflare Data Enrichment";
  if (callback) getSegment("track")(event, props, callback);
  else getSegment("track")(event, props);
}

/**
 * @param {string} eventType
 * @param {Record<string, string>} props
 */
export function trackFilterElementEvent(eventType, props) {
  /** @type {Record<string, string>}*/
  const eventNameMap = {
    input: "Filter Item Selected",
    change: "Filter Item Selected",
    focus: "Filter Menu Opened",
  };
  const eventPropsKeys = ["type", "field_name", "text"];

  const eventName = eventNameMap[eventType] ?? "Filter Item Undefined Event";
  const eventProps = Object.keys(props)
    .filter((key) => eventPropsKeys.includes(key))
    .reduce((obj, key) => ({ ...obj, [key]: props[key] }), {});

  return getSegment("track")(eventName, eventProps);
}

/**
 * @param {string | null} formType
 * @param {import('./lib/form-prefill').formDataType} formData
 */
export const trackFormDataSaved = (formType, formData) => {
  const eventProps = {
    form_type: formType,
    fields: Object.keys(formData),
  };
  getSegment("track")("Form Data Saved", eventProps);
};

/**
 * @param {string | null} formType
 * @param {string[]} fields
 */
export const trackFormPrefilled = (formType, fields) => {
  const eventProps = {
    form_type: formType,
    fields: fields,
  };
  getSegment("track")("Form Prefilled", eventProps);
};

/**
 * @param {string | null} formType
 */
export const trackFormHidden = (formType) => {
  const eventProps = {
    form_type: formType,
  };
  getSegment("track")("Form Hidden", eventProps);
};
